import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { chatService, MESSAGE_PAGE_SIZE } from '../services/chatService';
import { Message } from '../../../types';
import { UploadedMedia } from '../../../lib/api';
import { useSocketEvent, SOCKET_EVENTS } from '../../../lib/websocket';
import { notificationSound } from '../../../lib/notificationSound';

export function useMessages(conversationId: string | null) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const { data: fetchedMessages, isLoading } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: () => chatService.getMessages(conversationId!),
    enabled: !!conversationId,
  });

  useEffect(() => {
    if (fetchedMessages) {
      setMessages(fetchedMessages);
      setHasMore(fetchedMessages.length >= MESSAGE_PAGE_SIZE);
    }
  }, [fetchedMessages]);

  // Load older messages (scroll up)
  const loadMore = useCallback(async () => {
    if (!conversationId || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const older = await chatService.getMessages(conversationId, messages.length);
      if (older.length > 0) {
        setMessages((prev) => [...older, ...prev]);
      }
      setHasMore(older.length >= MESSAGE_PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  }, [conversationId, messages.length, loadingMore, hasMore]);

  // Listen for new messages via socket (dedup by id)
  useSocketEvent(SOCKET_EVENTS.MESSAGE_NEW, (data) => {
    if (data.conversationId === conversationId) {
      setMessages((prev) => {
        if (prev.some((m) => m.id === data.message.id)) return prev;
        return [...prev, data.message];
      });
      // Play sound for incoming messages (not own staff sends)
      if (data.message.sender !== 'staff') {
        notificationSound.playMessage();
      }
    }
  });

  // Apply edit broadcasts. Each conversation room only sees its own events
  // so the conversationId check here is belt-and-braces.
  useSocketEvent(SOCKET_EVENTS.MESSAGE_EDITED, (data) => {
    if (data.conversationId !== conversationId) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === data.messageId
          ? { ...m, content: data.content, editedAt: data.editedAt, editedByName: data.editedBy }
          : m,
      ),
    );
  });

  // Soft-delete broadcasts. Keeps the row so the timeline order is intact;
  // MessageItem swaps the bubble for a 'removed by X' placeholder.
  useSocketEvent(SOCKET_EVENTS.MESSAGE_DELETED, (data) => {
    if (data.conversationId !== conversationId) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === data.messageId
          ? { ...m, deletedAt: data.deletedAt, deletedByName: data.deletedBy }
          : m,
      ),
    );
  });

  const sendMutation = useMutation({
    mutationFn: ({ content, media }: { content: string; media?: UploadedMedia }) =>
      chatService.sendMessage(conversationId!, content, media),
    onSuccess: (message) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === message.id)) return prev;
        return [...prev, message];
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ messageId, content }: { messageId: string; content: string }) =>
      chatService.editMessage(messageId, content),
    onSuccess: ({ message }) => {
      // The socket broadcast also patches the list; this fallback covers
      // the rare case where the socket round-trip hasn't landed yet.
      setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, ...message } : m)));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (messageId: string) => chatService.deleteMessage(messageId),
    onSuccess: ({ message }) => {
      setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, ...message } : m)));
    },
  });

  const sendMessage = useCallback(
    (content: string, media?: UploadedMedia) => sendMutation.mutate({ content, media }),
    [sendMutation],
  );

  return {
    messages,
    isLoading,
    sendMessage,
    isSending: sendMutation.isPending,
    editMessage: editMutation.mutateAsync,
    isEditing: editMutation.isPending,
    deleteMessage: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    hasMore,
    loadMore,
    loadingMore,
  };
}
